/**
 * Recall is modulation-aware: Rig States' locked semantics #3
 * (`.notepads/rig-states-design.md`), "the #1 real-gig failure". Recalling a
 * look makes it the SOLE driver of its named targets, so an LFO still sweeping
 * one of them is stopped first; otherwise the LFO's next step overwrites the
 * value the look just set, and the look appears not to have worked.
 *
 * LFOs only (2026-10-18). Macros are the other half of #3, but OPButtons has no
 * way to stop a running Macro on OPBridge (`httpClient.ts` arms and pushes
 * them; there is no stop route), so that half is an OPBridge ask, not built.
 *
 * Pure matching here; `rigState/store.ts::recallLook` does the stopping.
 */

import { DEFAULT_PORT_TOKEN } from '../liveState/sendCache.ts';

/** A CC target id, `midi:<port>:cc:<channel 1..16>:<cc>`
 *  (`liveState/sendCache.ts::midiCcTargetId`). Null for anything else. */
export function parseCcTarget(targetId: string): { port: string; channel: number; cc: number } | null {
  const m = /^midi:(.+):cc:(\d+):(\d+)$/.exec(targetId);
  if (!m) return null;
  return { port: m[1]!, channel: Number(m[2]), cc: Number(m[3]) };
}

export interface LfoLike { id: string; channel: number; ccNumber: number }

/** The LFOs that drive any of these targets. An LFO sends on its bridge's
 *  default MIDI output (`dispatch.ts::armLfoOnBridge`), so it matches a target
 *  on the `@default` port token or on any bridge's default port by name (a
 *  look captured before the token named the port). An LFO's channel is 0..15,
 *  a target's 1..16. */
export function lfosDriving(targetIds: readonly string[], lfos: readonly LfoLike[], defaultPorts: readonly string[]): LfoLike[] {
  const wanted = targetIds.map(parseCcTarget).filter((t): t is NonNullable<typeof t> => t !== null)
    .filter((t) => t.port === DEFAULT_PORT_TOKEN || defaultPorts.includes(t.port));
  return lfos.filter((l) => wanted.some((t) => t.channel === l.channel + 1 && t.cc === l.ccNumber));
}
